import { memo, useCallback, useEffect, useState } from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/utils";
import { RENTAL_STATUS_LABELS } from "./RentalStatusBadge";
import { RENTAL_TYPE_LABELS } from "./RentalTypeBadge";
import type { RentalListFilters } from "../hooks/useRentalList";
import { RentalStatus, RentalType } from "@/shared/types/enums";

const STATUSES: RentalStatus[] = [
  RentalStatus.INCOMPLETE,
  RentalStatus.COMPLETE,
  RentalStatus.ON_STREET,
  RentalStatus.CANCELED,
  RentalStatus.FINISHED,
];

const RENTAL_TYPES: RentalType[] = [RentalType.SUIT, RentalType.GOWN];

interface RentalFiltersProps {
  filters: RentalListFilters;
  loading: boolean;
  onApply: (filters: RentalListFilters) => void;
  onClear: () => void;
}

// Busca por ID é exata e ignora os demais filtros (ver useRentalList) —
// com o ID preenchido, os outros campos ficam desabilitados. Busca por
// contrato ignora só o período (evita esconder contratos antigos).
export const RentalFilters = memo(function RentalFilters({
  filters,
  loading,
  onApply,
  onClear,
}: RentalFiltersProps) {
  const [draft, setDraft] = useState<RentalListFilters>(filters);

  useEffect(() => {
    setDraft(filters);
  }, [filters]);

  const toggleStatus = useCallback((status: RentalStatus) => {
    setDraft((prev) => {
      const current = prev.statuses ?? [];
      const next = current.includes(status)
        ? current.filter((s) => s !== status)
        : [...current, status];
      return { ...prev, statuses: next.length ? next : undefined };
    });
  }, []);

  const toggleType = useCallback((type: RentalType) => {
    setDraft((prev) => {
      const current = prev.rentalTypes ?? [];
      const next = current.includes(type)
        ? current.filter((t) => t !== type)
        : [...current, type];
      return { ...prev, rentalTypes: next.length ? next : undefined };
    });
  }, []);

  // Mesmos predicados (truthiness) usados por useRentalList/buildSummaryParams
  // — divergir aqui faria a UI prometer um modo sem a request executá-lo.
  const searchingById = !!draft.rentalId;
  const searchingByContract = !searchingById && !!draft.contractNumbers?.length;
  const othersDisabled = loading || searchingById;
  const datesDisabled = othersDisabled || searchingByContract;

  const isInvalid =
    !searchingById &&
    !searchingByContract &&
    (draft.startDate === "" ||
      draft.endDate === "" ||
      draft.endDate < draft.startDate);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="space-y-2">
          <Label htmlFor="rental-filter-start">Data inicial</Label>
          <Input
            id="rental-filter-start"
            type="date"
            value={draft.startDate}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, startDate: e.target.value }))
            }
            disabled={datesDisabled}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="rental-filter-end">Data final</Label>
          <Input
            id="rental-filter-end"
            type="date"
            value={draft.endDate}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, endDate: e.target.value }))
            }
            disabled={datesDisabled}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="rental-filter-id">ID do aluguel</Label>
          <Input
            id="rental-filter-id"
            type="text"
            inputMode="numeric"
            placeholder="Ex.: 123"
            value={draft.rentalId ?? ""}
            onChange={(e) => {
              const raw = e.target.value.trim();
              if (raw === "") {
                setDraft((prev) => ({ ...prev, rentalId: undefined }));
                return;
              }
              // Valida a string crua (não Number()): sem notação científica
              // nem estado fantasma de type=number. Só inteiro positivo no
              // range de Integer entra no draft — o modo ID (searchingById)
              // e o bind no backend dependem disso.
              if (!/^\d+$/.test(raw)) return;
              const parsed = Number(raw);
              if (parsed < 1 || parsed > 2147483647) return;
              setDraft((prev) => ({ ...prev, rentalId: parsed }));
            }}
            disabled={loading}
            aria-describedby={
              searchingById ? "rental-filter-id-hint" : undefined
            }
          />
          {searchingById && (
            <p
              id="rental-filter-id-hint"
              role="status"
              className="text-xs text-muted-foreground"
            >
              Ao buscar por ID, os demais filtros são ignorados.
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="rental-filter-contract">Número do contrato</Label>
          <Input
            id="rental-filter-contract"
            placeholder="Ex.: 1047"
            value={draft.contractNumbers?.[0] ?? ""}
            onChange={(e) => {
              const value = e.target.value.trim();
              setDraft((prev) => ({
                ...prev,
                contractNumbers: value ? [value] : undefined,
              }));
            }}
            disabled={othersDisabled}
            aria-describedby={
              searchingByContract ? "rental-filter-contract-hint" : undefined
            }
          />
          {searchingByContract && (
            <p
              id="rental-filter-contract-hint"
              role="status"
              className="text-xs text-muted-foreground"
            >
              Ao buscar por contrato, o período é ignorado.
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="rental-filter-client">Cliente</Label>
          <Input
            id="rental-filter-client"
            placeholder="Nome do cliente"
            value={draft.clientName ?? ""}
            onChange={(e) =>
              setDraft((prev) => ({
                ...prev,
                clientName: e.target.value || undefined,
              }))
            }
            disabled={othersDisabled}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Status</Label>
        <div className="flex flex-wrap gap-2">
          {STATUSES.map((status) => {
            const selected = draft.statuses?.includes(status) ?? false;
            return (
              <button
                key={status}
                type="button"
                onClick={() => toggleStatus(status)}
                disabled={othersDisabled}
                aria-pressed={selected}
                className={cn(
                  "px-3 py-1.5 text-sm rounded-full border transition-colors",
                  selected
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-muted-foreground border-border hover:bg-accent",
                  othersDisabled && "opacity-50 cursor-not-allowed",
                )}
              >
                {RENTAL_STATUS_LABELS[status]}
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Tipo</Label>
        <div className="flex flex-wrap gap-2">
          {RENTAL_TYPES.map((type) => {
            const selected = draft.rentalTypes?.includes(type) ?? false;
            return (
              <button
                key={type}
                type="button"
                onClick={() => toggleType(type)}
                disabled={othersDisabled}
                aria-pressed={selected}
                className={cn(
                  "px-3 py-1.5 text-sm rounded-full border transition-colors",
                  selected
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-muted-foreground border-border hover:bg-accent",
                  othersDisabled && "opacity-50 cursor-not-allowed",
                )}
              >
                {RENTAL_TYPE_LABELS[type]}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={onClear}
          disabled={loading}
          className="gap-2"
        >
          <X className="w-4 h-4" />
          Limpar
        </Button>
        <Button
          onClick={() =>
            onApply({
              ...draft,
              clientName: draft.clientName?.trim() || undefined,
            })
          }
          disabled={loading || isInvalid}
          className="gap-2"
        >
          <Search className="w-4 h-4" />
          Aplicar
        </Button>
      </div>
    </div>
  );
});
